import { configureStore, Middleware } from '@reduxjs/toolkit'
import { FetchBaseQueryError, setupListeners } from '@reduxjs/toolkit/query'
import { useEffect } from 'react'
import createSagaMiddleware from 'redux-saga'

import { LOGOUT_ACTION, RESET_ACTION } from '@/constants/actions'

import {
  api,
  GetByTenantAccountInprogressAndAccountIdentifierApiArg,
  GetByTenantAccountInprogressAndAccountIdentifierApiResponse,
  GetByTenantOrderConfirmedApiArg,
  GetByTenantOrderConfirmedApiResponse,
  GetByTenantOrderOutstandingAndAssetIdApiArg,
  GetByTenantOrderOutstandingAndAssetIdApiResponse,
  PostByTenantAccountSubmitAndAccountIdentifierApiArg,
  PostByTenantAccountSubmitAndAccountIdentifierApiResponse,
  PostByTenantOrderConfirmationApiArg,
  PostByTenantOrderConfirmationApiResponse,
  PostByTenantOrderCorrectionApiArg,
  PostByTenantOrderCorrectionApiResponse
} from '../api'
import { PATH } from '../const'
import { rootSaga as assetsSaga } from '../modules/asset-admin/store/root-saga'
import { assetsReducers } from '../modules/asset-admin/store/store'
import { rootSaga as bankUsersSaga } from '../modules/bank-users/store/root-saga'
import { bankUsersReducers } from '../modules/bank-users/store/store'
import { rootSaga as cashInstrumentsSaga } from '../modules/cash-instrument-admin/store/root-saga'
import { cashInstrumentsReducer } from '../modules/cash-instrument-admin/store/store'
import { rootSaga as draftTradesSaga } from '../modules/draft-trades/store/root-saga'
import { draftTradesReducers } from '../modules/draft-trades/store/store'
import { rootSaga as backOfficeSaga } from '../modules/funds-backoffice/store/root-saga'
import { backOfficeReducers } from '../modules/funds-backoffice/store/store'
import { rootSaga as gapRiskSaga } from '../modules/gap-risk/store/root-saga'
import { gapRiskReducers } from '../modules/gap-risk/store/store'
import { rootSaga as portfoliosSaga } from '../modules/model-portfolio-admin/store/root-saga'
import { portfoliosReducer } from '../modules/model-portfolio-admin/store/store'
import { rootSaga as stragiesSaga } from '../modules/strategy-admin/store/root-saga'
import { strategiesReducer } from '../modules/strategy-admin/store/store'
import { rootSaga as tradeBuilderSaga } from '../modules/tradebuilder-v2/store/root-saga'
import { tradeBuilderReducers } from '../modules/tradebuilder-v2/store/store'

const LOGGED_OUT_PATHS = [
  '/login',
  '/reset-password',
  '/two-factor',
  '/account-locked',
  '/confirm-account/',
  '/set-up-two-factor'
]

export const authMiddleware: Middleware = () => next => action => {
  const is401 =
    action &&
    typeof action === 'object' &&
    'meta' in action &&
    action.meta &&
    typeof action.meta === 'object' &&
    'baseQueryMeta' in action.meta &&
    action.meta.baseQueryMeta &&
    typeof action.meta.baseQueryMeta === 'object' &&
    'response' in action.meta.baseQueryMeta &&
    action.meta.baseQueryMeta.response &&
    typeof action.meta.baseQueryMeta.response === 'object' &&
    'status' in action.meta.baseQueryMeta.response &&
    action.meta.baseQueryMeta.response.status === 401
  // The user request is made in one of two places:
  //   - when the user first logs in
  //   - when the user first loads any page
  // What would happen without this check is:
  //   - user is not logged in
  //   - user hits login page
  //   - app tries to check who the user is, calling /usermanager/current
  //   - app sees a 401 response and tries to log the user out and navigate to /login
  //   - back to step 1 and repeat
  const isUserRequest =
    action &&
    typeof action === 'object' &&
    'meta' in action &&
    action.meta &&
    typeof action.meta === 'object' &&
    'baseQueryMeta' in action.meta &&
    action.meta.baseQueryMeta &&
    typeof action.meta.baseQueryMeta === 'object' &&
    'request' in action.meta.baseQueryMeta &&
    action.meta.baseQueryMeta.request &&
    typeof action.meta.baseQueryMeta.request === 'object' &&
    'url' in action.meta.baseQueryMeta.request &&
    typeof action.meta.baseQueryMeta.request.url === 'string' &&
    action.meta.baseQueryMeta.request.url.includes('/usermanager/current')

  // We don't want to redirect the user on 401 if they are already logged out
  const isAlreadyLoggedOut = LOGGED_OUT_PATHS.some(path => window.location.pathname.includes(path))

  if (is401 && !isUserRequest && !isAlreadyLoggedOut) {
    window.location.replace(PATH.logout)
  } else {
    return next(action)
  }
}

const sagaMiddleware = createSagaMiddleware()

const addTagTypes = [
  'Assets',
  'Confirmed',
  'Outstanding',
  'Inprogress',
  'Note',
  'ApproveRejectNote',
  'ApproveRejectTrade',
  'TradeSwitch'
]

const invalidateIfSuccessful =
  (tags: typeof addTagTypes) => (result: unknown, error: FetchBaseQueryError) =>
    error ? [] : tags

const enhancedApi = api.enhanceEndpoints({
  addTagTypes,
  endpoints: {
    getByTenantAccountInprogress: {
      providesTags: ['Inprogress']
    },
    postByTenantNoteAndNoteIdChangeCounterpartyNewCounterpartyId: {
      invalidatesTags: invalidateIfSuccessful(['Note'])
    },
    postByTenantNoteAndNoteIdChangeBusinessunitNewBusinessUnitId: {
      invalidatesTags: invalidateIfSuccessful(['Note'])
    },
    getByTenantAssetListOrderAlert: {
      providesTags: ['Assets']
    },
    getByTenantAccount: {
      providesTags: ['Note']
    },
    getByTenantNote: {
      providesTags: ['Note']
    },
    getByTenantAccountDashboard: {
      providesTags: ['Note']
    },
    getByTenantAccountAndAccountSourceAccountIdentifier: {
      providesTags: ['Note']
    },
    getByTenantOrderOutstandingAndAssetId: {
      providesTags: (
        result: GetByTenantOrderOutstandingAndAssetIdApiResponse,
        error,
        arg: GetByTenantOrderOutstandingAndAssetIdApiArg
      ) =>
        result
          ? [
              ...result.map(x => ({ type: 'Outstanding' as const, id: x.orderId })),
              { type: 'Outstanding', id: arg.assetId }
            ]
          : [{ type: 'Outstanding', id: arg.assetId }]
    },

    getByTenantOrderConfirmed: {
      providesTags: (
        result: GetByTenantOrderConfirmedApiResponse,
        error,
        arg: GetByTenantOrderConfirmedApiArg
      ) =>
        result
          ? [
              ...result.data.map(x => ({ type: 'Confirmed' as const, id: x.orderId })),
              { type: 'Confirmed', id: arg.assetId }
            ]
          : [{ type: 'Confirmed', id: arg.assetId }]
    },

    postByTenantOrderConfirmation: {
      invalidatesTags: (
        result: PostByTenantOrderConfirmationApiResponse,
        error,
        arg: PostByTenantOrderConfirmationApiArg
      ) => [
        'Assets',
        { type: 'Outstanding', id: arg.apiNewAggregatedOrderConfirmation.orderId },
        { type: 'Confirmed', id: arg.apiNewAggregatedOrderConfirmation.orderId }
      ]
    },

    postByTenantOrderCorrection: {
      invalidatesTags: (
        result: PostByTenantOrderCorrectionApiResponse,
        error,
        arg: PostByTenantOrderCorrectionApiArg
      ) => [{ type: 'Confirmed', id: arg.apiNewAggregatedOrderCorrection.assetId }]
    },

    postByTenantAccountUpdateSave: {
      invalidatesTags: ['Note']
    },
    getByTenantNoteInprogressCount: {
      providesTags: ['ApproveRejectNote', 'ApproveRejectTrade']
    },
    // Trade approvals
    postByTenantAccountSubmitAndAccountIdentifier: {
      invalidatesTags: (
        result: PostByTenantAccountSubmitAndAccountIdentifierApiResponse,
        error,
        arg: PostByTenantAccountSubmitAndAccountIdentifierApiArg
      ) =>
        error
          ? []
          : [
              { type: 'TradeSwitch', id: arg.accountIdentifier },
              { type: 'ApproveRejectTrade' },
              { type: 'Inprogress' }
            ]
    },
    postByTenantAccountApproveEconomicsAndAccountIdentifier: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectTrade'])
    },
    postByTenantAccountApproveAndAccountIdentifier: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectTrade'])
    },
    postByTenantAccountRejectAndAccountIdentifier: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectTrade'])
    },
    postByTenantAccountCommitAndAccountIdentifier: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectTrade'])
    },
    postByTenantAccountCommitClientAndAccountIdentifier: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectTrade'])
    },
    postByTenantAccountConfirmAndAccountIdentifier: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectTrade', 'Inprogress'])
    },
    postByTenantAccountActivateAndAccountIdentifier: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectTrade'])
    },
    // Note approvals
    postByTenantNoteInstructionSubmit: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectNote'])
    },
    postByTenantNoteAndNoteIdInstructionReject: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectNote'])
    },
    postByTenantNoteAndNoteIdInstructionApprove: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectNote'])
    },
    postByTenantNoteAndNoteIdInstructionApproveCommitment: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectNote'])
    },
    postByTenantNoteAndNoteIdInstructionApproveCommitmentClient: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectNote'])
    },
    postByTenantNoteAndNoteIdInstructionApproveSettlement: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectNote'])
    },
    postByTenantNoteAndNoteIdInstructionActivate: {
      invalidatesTags: invalidateIfSuccessful(['ApproveRejectNote'])
    },
    postByTenantNoteAndNoteIdChangeIscollateralforloanIsCollateralForLoan: {
      invalidatesTags: invalidateIfSuccessful(['Note'])
    },
    getByTenantNoteInprogress: {
      providesTags: ['ApproveRejectNote']
    },
    getByTenantAccountInprogressAndAccountIdentifier: {
      providesTags: (
        result: GetByTenantAccountInprogressAndAccountIdentifierApiResponse,
        error,
        arg: GetByTenantAccountInprogressAndAccountIdentifierApiArg
      ) => [{ type: 'TradeSwitch', id: arg.accountIdentifier }]
    }
  }
})

export const store = configureStore({
  reducer: {
    [enhancedApi.reducerPath]: enhancedApi.reducer,
    ...tradeBuilderReducers,
    ...draftTradesReducers,
    ...bankUsersReducers,
    ...backOfficeReducers,
    ...assetsReducers,
    ...portfoliosReducer,
    ...strategiesReducer,
    ...gapRiskReducers,
    ...cashInstrumentsReducer
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware().concat(enhancedApi.middleware, sagaMiddleware, authMiddleware),
  devTools: import.meta.env.DEV
})

sagaMiddleware.run(tradeBuilderSaga)
sagaMiddleware.run(draftTradesSaga)
sagaMiddleware.run(bankUsersSaga)
sagaMiddleware.run(backOfficeSaga)
sagaMiddleware.run(assetsSaga)
sagaMiddleware.run(portfoliosSaga)
sagaMiddleware.run(stragiesSaga)
sagaMiddleware.run(cashInstrumentsSaga)
sagaMiddleware.run(gapRiskSaga)

export type Dispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>

export const resetStoreLogout = () => {
  store.dispatch({ type: LOGOUT_ACTION })
  store.dispatch(enhancedApi.util.resetApiState())
}

export const resetStore = () => {
  store.dispatch({ type: RESET_ACTION })
}

setupListeners(store.dispatch)

export const useResetTradeBuilder = () => {
  useEffect(() => {
    resetStore()
    setTimeout(() => {
      resetStore()
    }, 0)
  }, [])
}
